import React from 'react'
import { useNavigate } from 'react-router'
import { TISACard } from '@/components/tisa/TISACard'
import { TISAButton } from '@/components/tisa/TISAButton'

// Navigation wrapper component for the error boundary
function ErrorBoundaryContent({
  error,
  onReset
}: {
  error?: Error;
  onReset: () => void
}) {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-tisa-red-50 to-tisa-yellow-50 flex items-center justify-center p-4">
      <TISACard size="lg" className="max-w-2xl">
        <div className="text-center space-y-6">
          <div className="text-child-4xl">😕</div>
          <div>
            <h2 className="text-child-2xl font-bold text-tisa-red-600 mb-2">
              Oops! Something went wrong
            </h2>
            <p className="text-child-base text-muted-foreground">
              Don't worry, it's not your fault. The app had a little hiccup.
            </p>
          </div>

          {error && (
            <details className="text-left bg-muted p-4 rounded-lg">
              <summary className="text-child-sm font-semibold cursor-pointer">
                Technical details (for teachers)
              </summary>
              <pre className="text-xs mt-2 overflow-auto">
                {error.message}
                {error.stack}
              </pre>
            </details>
          )}

          <div className="space-y-3">
            <TISAButton
              onClick={onReset}
              variant="theme-blue"
              size="lg"
              className="w-full"
            >
              Try Again
            </TISAButton>
            <TISAButton
              onClick={() => navigate('/')}
              variant="outline"
              size="md"
              className="w-full"
            >
              Go Home
            </TISAButton>
          </div>

          <p className="text-child-xs text-muted-foreground">
            If this keeps happening, please ask your teacher or librarian for help.
          </p>
        </div>
      </TISACard>
    </div>
  )
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryContent
          error={this.state.error}
          onReset={() => this.setState({ hasError: false })}
        />
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary